import { useEffect, useState } from 'react';
import { Row, Col, Card, Statistic, List, Tag, Typography, Avatar } from 'antd';
import { 
  ProjectOutlined, 
  CheckCircleOutlined, 
  ClockCircleOutlined, 
  CalendarOutlined, 
  MessageOutlined,
  TeamOutlined,
  FileTextOutlined,
  RiseOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import LinkifyText from '../components/LinkifyText';
import { TasksService } from '../api/services/tasks.service';
import { CalendarService } from '../api/services/calendar.service';
import { ActivityService } from '../api/services/activity.service';
import { UsersService } from '../api/services/users.service';
import { ProjectsService } from '../api/services/projects.service';

export default function DashboardPage() {
  const [tasks, setTasks] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [events, setEvents] = useState([]);
  const [feed, setFeed] = useState([]);

  useEffect(() => {
    TasksService.getTasks({ status: 'in_progress' }).then(r => setTasks(r.data)).catch(() => {});
    TasksService.getTasks().then(r => setAllTasks(r.data)).catch(() => {});
    UsersService.getUsers().then(r => setUsers(r.data)).catch(() => {});
    ProjectsService.getProjects().then(r => setProjects(r.data)).catch(() => {});
    CalendarService.getEvents().then(r => setEvents(r.data.slice(0, 5))).catch(() => {});
    ActivityService.getFeed().then(r => setFeed(r.data)).catch(() => {});
  }, []);

  const statusColors = { new: 'blue', in_progress: 'orange', review: 'purple', done: 'green', cancelled: 'red' };

  const statCards = [
    {
      title: 'Активные задачи',
      value: tasks.length,
      icon: <ProjectOutlined />,
      gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
      link: '/tasks',
    },
    {
      title: 'Предстоящие события',
      value: events.length,
      icon: <CalendarOutlined />,
      gradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
      link: '/calendar',
    },
    {
      title: 'Проекты',
      value: projects.length,
      icon: <FileTextOutlined />,
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      link: '/projects',
    },
    {
      title: 'Сотрудники',
      value: users.length,
      icon: <TeamOutlined />,
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      link: '/users',
    },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Панель управления</h1>
        <p className="page-subtitle">Добро пожаловать в систему ТЕРРА-СУП</p>
      </div>

      <Row gutter={[20, 20]} style={{ marginBottom: 32 }}>
        {statCards.map((stat, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <Link to={stat.link} style={{ textDecoration: 'none' }}>
              <Card 
                className="stat-card card-hover"
                style={{ 
                  borderRadius: 16,
                  overflow: 'hidden',
                  cursor: 'pointer',
                }}
                styles={{ body: { padding: 24 } }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: 14, color: '#64748b', marginBottom: 8 }}>
                      {stat.title}
                    </div>
                    <div style={{ fontSize: 32, fontWeight: 700, color: '#0f172a', lineHeight: 1 }}>
                      {stat.value}
                    </div>
                  </div>
                  <div style={{
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    background: stat.gradient,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 24,
                    color: 'white',
                  }}>
                    {stat.icon}
                  </div>
                </div>
              </Card>
            </Link>
          </Col>
        ))}
      </Row>

      <Row gutter={[20, 20]}>
        <Col xs={24} lg={12}>
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CheckCircleOutlined style={{ color: '#6366f1' }} />
                <span style={{ fontWeight: 600 }}>Мои задачи</span>
              </div>
            }
            style={{ marginBottom: 20, borderRadius: 16 }}
            styles={{ body: { padding: 0 } }}
            extra={<Link to="/tasks" style={{ color: '#6366f1' }}>Все задачи</Link>}
          >
            <List
              dataSource={tasks.slice(0, 10)}
              renderItem={(item) => (
                <List.Item style={{ padding: '16px 24px', border: 'none', borderBottom: '1px solid #f1f5f9' }}>
                  <List.Item.Meta
                    title={
                      <Link to={`/tasks?taskId=${item.id}`} style={{ color: '#0f172a', fontWeight: 500 }}>
                        {item.title}
                      </Link>
                    }
                    description={
                      <Tag 
                        color={statusColors[item.status]} 
                        style={{ borderRadius: 6, marginTop: 4 }}
                      >
                        {item.status === 'new' && 'Новая'}
                        {item.status === 'in_progress' && 'В работе'}
                        {item.status === 'review' && 'На проверке'}
                        {item.status === 'done' && 'Готово'}
                        {item.status === 'cancelled' && 'Отменена'}
                      </Tag>
                    }
                  />
                </List.Item>
              )}
              locale={{ emptyText: 'Нет активных задач' }}
            />
          </Card>

          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CalendarOutlined style={{ color: '#06b6d4' }} />
                <span style={{ fontWeight: 600 }}>Ближайшие события</span>
              </div>
            }
            style={{ borderRadius: 16 }}
            styles={{ body: { padding: 0 } }}
            extra={<Link to="/calendar" style={{ color: '#6366f1' }}>Все события</Link>}
          >
            <List
              dataSource={events}
              renderItem={(item) => (
                <List.Item style={{ padding: '16px 24px', border: 'none', borderBottom: '1px solid #f1f5f9' }}>
                  <List.Item.Meta
                    title={<Link to="/calendar" style={{ color: '#0f172a', fontWeight: 500 }}>{item.title}</Link>}
                    description={
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                        <ClockCircleOutlined style={{ color: '#64748b', fontSize: 12 }} />
                        <span style={{ color: '#64748b', fontSize: 13 }}>
                          {new Date(item.start_time).toLocaleString('ru-RU', { 
                            day: '2-digit', 
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                    }
                  />
                </List.Item>
              )}
              locale={{ emptyText: 'Нет событий' }}
            />
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <MessageOutlined style={{ color: '#10b981' }} />
                <span style={{ fontWeight: 600 }}>Лента активности</span>
              </div>
            }
            style={{ borderRadius: 16, height: '100%' }}
            styles={{ body: { padding: 0, maxHeight: 800, overflowY: 'auto' } }}
          >
            <List
              dataSource={feed}
              renderItem={(item) => (
                <List.Item style={{ padding: '16px 24px', border: 'none', borderBottom: '1px solid #f1f5f9' }}>
                  <List.Item.Meta
                    avatar={
                      <Avatar 
                        style={{ 
                          background: item.type === 'task' 
                            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
                            : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        }}
                        icon={item.type === 'task' ? <CheckCircleOutlined /> : <MessageOutlined />} 
                      />
                    }
                    title={
                      <div style={{ fontWeight: 600, color: '#0f172a' }}>
                        {item.sender_name}
                        <span style={{ fontWeight: 400, color: '#64748b', fontSize: 13, marginLeft: 8 }}>
                          {item.type === 'task' ? (
                            <>в задаче <Link to={`/tasks?taskId=${item.task_id}`} style={{ color: '#6366f1' }}>{item.task_title || `#${item.task_id}`}</Link></>
                          ) : (
                            `в чате ${item.room_name}`
                          )}
                        </span>
                      </div>
                    }
                    description={
                      <div>
                        <div style={{ whiteSpace: 'pre-wrap', color: '#0f172a', marginBottom: 4, lineHeight: 1.5 }}>
                          <LinkifyText text={item.content} style={{ display: 'inline' }} />
                        </div>
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>
                          {new Date(item.created_at).toLocaleString('ru-RU', {
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </div>
                    }
                  />
                </List.Item>
              )}
              locale={{ emptyText: 'Нет упоминаний' }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}
